import {
  type AppError,
  InvalidTokenError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {
  FollowCompanyCountSchema,
  companyInfoSchema,
  groupedCompaniesSchema,
} from '../entities/company/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

export type GetCompanyInfoResponse = z.infer<typeof companyInfoSchema>;
export type ResultGetCompanyInfo = ResultWithOauthToken<
  GetCompanyInfoResponse,
  AppError
>;

type GetCompanyInfoArgs = {
  companyId: number;
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export const getCompanyInfo = async ({
  companyId,
  cookies,
  version = DEFAULT_API_VERSION,
}: GetCompanyInfoArgs): Promise<ResultGetCompanyInfo> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    return defaultClient.get(
      `/${version}/companies/${companyId}/info`,
      companyInfoSchema,
    );
  };

  const result = sendApiRequest({
    cookies,
    request,
    authRequired: false,
  });

  return result;
};

type GetCompanyInfoOptions = {
  config?: UseQueryOptions<ResultGetCompanyInfo>;
} & Omit<GetCompanyInfoArgs, 'cookies'>;

export const useGetCompanyInfo = ({
  companyId,
  version = DEFAULT_API_VERSION,
  config,
}: GetCompanyInfoOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultGetCompanyInfo>({
    ...config,
    queryKey: ['getCompanyInfo', companyId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getCompanyInfo({companyId, cookies, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * FollowCompany
 * Postでフォローする
 * Deleteでフォローを外す
 */

export type FollowCompanyCountResponse = z.infer<
  typeof FollowCompanyCountSchema
>;
export type FollowCompanyCountReturnValueType = ResultWithOauthToken<
  FollowCompanyCountResponse,
  AppError
>;

type UpdateFollowCompanyArgs = {
  companyId: number;
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type UseUpdateFollowCompanyOptions = {
  config?: UseQueryOptions<FollowCompanyCountReturnValueType>;
  onSuccessFunc?: ({followCompanyCount}: {followCompanyCount: number}) => void;
  onErrorFunc?: () => void;
} & Omit<UpdateFollowCompanyArgs, 'cookies' | 'companyId'>;

export const updateFollowCompany = async ({
  cookies,
  companyId,
  version = DEFAULT_API_VERSION,
}: UpdateFollowCompanyArgs): Promise<FollowCompanyCountReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    return defaultClient.post(
      `/${version}/companies/${companyId}/follow`,
      FollowCompanyCountSchema,
      {},
    );
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
  });

  return result;
};

export const useUpdateFollowCompany = ({
  version = DEFAULT_API_VERSION,
  onSuccessFunc,
  onErrorFunc,
}: UseUpdateFollowCompanyOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation<
    FollowCompanyCountReturnValueType,
    unknown,
    {companyId: number; scoutPopupShowDate?: string}
  >({
    async mutationFn({companyId}) {
      const cookies = parseCookies();
      const result = await updateFollowCompany({companyId, cookies, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    onSuccess(data) {
      if (onSuccessFunc && data.ok) {
        onSuccessFunc({
          followCompanyCount: data.value.followCompanyCount,
        });
      }
    },
    onError() {
      if (onErrorFunc) {
        onErrorFunc();
      }
    },
  });
};

export const deleteFollowCompany = async ({
  companyId,
  cookies,
  version = DEFAULT_API_VERSION,
}: UpdateFollowCompanyArgs): Promise<FollowCompanyCountReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    return defaultClient.delete(
      `/${version}/companies/${companyId}/follow`,
      FollowCompanyCountSchema,
    );
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
  });

  return result;
};

export const useDeleteFollowCompany = ({
  version = DEFAULT_API_VERSION,
  onSuccessFunc,
  onErrorFunc,
}: UseUpdateFollowCompanyOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation<
    FollowCompanyCountReturnValueType,
    unknown,
    {companyId: number}
  >({
    async mutationFn({companyId}) {
      const cookies = parseCookies();
      const result = await deleteFollowCompany({companyId, cookies, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    onSuccess(data) {
      if (onSuccessFunc && data.ok) {
        onSuccessFunc({followCompanyCount: data.value.followCompanyCount});
      }
    },
    onError() {
      if (onErrorFunc) {
        onErrorFunc();
      }
    },
  });
};

export type GetGroupedCompaniesResponse = z.infer<
  typeof groupedCompaniesSchema
>;
type ResultGetGroupedCompanies = Result<GetGroupedCompaniesResponse, AppError>;

type GetGroupedCompaniesArgs = {
  companyId: number;
  version?: string;
};

export const getGroupedCompanies = async ({
  companyId,
  version = DEFAULT_API_VERSION,
}: GetGroupedCompaniesArgs): Promise<ResultGetGroupedCompanies> => {
  const result = await defaultClient.get(
    `/${version}/companies/${companyId}/grouped-companies`,
    groupedCompaniesSchema,
  );
  return result;
};

type GetGroupedCompaniesOptions = {
  config?: UseQueryOptions<ResultGetGroupedCompanies>;
} & GetGroupedCompaniesArgs;

export const useGetGroupedCompanies = ({
  companyId,
  version = DEFAULT_API_VERSION,
  config,
}: GetGroupedCompaniesOptions) => {
  return useQuery<ResultGetGroupedCompanies>({
    ...config,
    queryKey: ['getGroupedCompanies', companyId],
    async queryFn() {
      return getGroupedCompanies({companyId, version});
    },
  });
};
